import React, { FC } from 'react'
import { getTriggerStyles } from './styles'
import { useIsInk } from '@ds/ui'
import { inkIconTokens } from '@ds/tokens'
import { PhoneInputSize } from './types'
import { useHtmlDir } from '@ds/react-utils'

type ReactButton = React.ButtonHTMLAttributes<HTMLButtonElement>

export interface MenuTriggerProps extends ReactButton {
  /** Provides a reference to the <button> element. */
  forwardedRef?: React.Ref<HTMLButtonElement>
  /** Will be rendered inside of a <button>. Most likely an icon or piece of text.  */
  children: React.ReactNode
  /** Determines if a caret icon should be displayed after the children. */
  showCaret?: boolean
  /** Size of the button to align with PhoneInput size. */
  size?: PhoneInputSize
}

/**
 * A button for opening a popup menu.
 * Font styles match those used in PhoneInput.
 *
 * @param props
 */
export const MenuTrigger: FC<MenuTriggerProps> = ({
  children,
  forwardedRef,
  showCaret = false,
  size: _size,
  disabled,
  ...rest
}) => {
  const isInk = useIsInk()
  const textDirection = useHtmlDir()
  const isRTL = textDirection === 'rtl'

  const { triggerBtn, triggerBtnChildren } = getTriggerStyles(isInk, isRTL)

  return (
    <button
      type="button"
      css={triggerBtn}
      disabled={disabled}
      ref={forwardedRef}
      {...rest}
    >
      <div css={triggerBtnChildren}>
        {children}
        {showCaret && renderCaret(isInk)}
      </div>
    </button>
  )
}

const renderCaret = (isInk: boolean) => (isInk ? <InkCaret /> : <OliveCaret />)

const OliveCaret: FC = () => (
  <svg
    css={{
      fill: inkIconTokens.iconColorDefault,
      height: 16,
      width: 16,
    }}
    className="SVGInline-svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    focusable="false"
  >
    <path d="M4 5h8l-4 6z" />
  </svg>
)

const InkCaret: FC = () => (
  <svg
    css={{
      fill: inkIconTokens.iconColorDefault,
      height: 20,
      width: 20,
    }}
    className="SVGInline-svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    focusable="false"
  >
    <path d="M10.62 12.73L15 8H5l4.38 4.73a.85.85 0 001.24 0z" />
  </svg>
)
